<script setup lang="ts">
/**
 * Carte d'une association — rayon /recherche/associations, pages ville /
 * région, « associations similaires », suggestions.
 * Présentation « vitrine » commune (VitrineCarte, Karim 2026-10-12) : logo,
 * nom, présentation, adresse et nombre d'activités posés sur la bannière.
 */
import { useRechercheHelpers } from "~/composables/useRechercheHelpers";
import { casseVille, villeDepuisTexte } from "@donoor/utils";
import { themeAffiche } from "~/utils/themesAffiches";

interface AssociationCarte {
  name: string;
  slug: string;
  logo: string | null;
  cover_image: string | null;
  // Bannière de secours (image de la cagnotte la plus dotée) servie par l'API
  // quand l'asso n'a pas de cover_image. Cf. publicOrganizationController.
  fallback_banner?: string | null;
  description: string | null;
  category: string | null;
  full_address: string | null;
  activities_count: number;
  is_verified?: boolean;
}

const props = withDefaults(
  defineProps<{
    org: AssociationCarte;
    /**
     * Carte du PREMIER écran : bannière et logo chargés tout de suite, en
     * priorité haute (audit lenteur images 2026-09-19).
     */
    priority?: boolean;
  }>(),
  { priority: false },
);

const { getImageUrl } = useRechercheHelpers();

/**
 * La VILLE seule, pas l'adresse complète (Karim 2026-10-15) : sur une carte,
 * « 12 rue des Lilas, 69003 Lyon » se coupait et n'apprenait rien de plus que
 * « Lyon ». Rien si la ville ne se lit pas dans l'adresse.
 */
const ville = computed(() => {
  const v = villeDepuisTexte(props.org.full_address);
  return v ? casseVille(v) : null;
});

const banniere = computed(() => props.org.cover_image || props.org.fallback_banner || null);

/** Thème ; rien pour « Autre » (n'apprend rien). */
const pastille = computed(() =>
  props.org.category && props.org.category !== "autre" ? themeAffiche(props.org.category) : "",
);
</script>

<template>
  <VitrineCarte
    :href="`/associations/${org.slug}`"
    :titre="org.name"
    :image="banniere"
    :alt="$t('association.banniereFixe.alt', { name: org.name || 'association' })"
    :pastille="pastille"
    :action="$t('commun.vitrine.actionAssociation')"
    :priority="priority"
    titre-une-ligne
  >
    <!-- Ni bannière ni cagnotte : ambiance colorée tirée du LOGO (logo flouté,
         ses couleurs visibles) plutôt qu'un aplat (Karim 2026-08-03). -->
    <template v-if="org.logo" #fond>
      <img
        :src="getImageUrl(org.logo, undefined, 200)"
        alt=""
        class="absolute inset-0 w-full h-full object-cover blur-2xl scale-125 opacity-90"
      />
    </template>

    <template #entete>
      <div class="relative inline-block">
        <OrgLogo
          :logo="org.logo"
          :name="org.name"
          size-class="w-11 h-11"
          rounded-class="rounded-xl"
          ring-class="border-2 border-white/80 shadow-sm"
          :priority="priority"
        />
        <!-- Badge « certifiée » au coin haut-droit du logo (asso vérifiée). -->
        <div
          v-if="org.is_verified"
          class="absolute -top-1.5 -right-1.5 rounded-full bg-white p-0.5 shadow-sm"
          :title="$t('association.recherche.associationCertifiee')"
        >
          <UIcon name="i-lucide-badge-check" class="block size-4 text-main2" />
        </div>
      </div>
    </template>

    <p v-if="org.description" class="-mt-1 mb-2 text-[13px] leading-snug text-white/75 line-clamp-2">
      {{ org.description }}
    </p>
    <div class="flex items-center justify-between gap-3 text-sm">
      <p v-if="ville" class="min-w-0 flex items-center gap-1.5 text-white/80">
        <UIcon name="i-lucide-map-pin" class="size-4 shrink-0" />
        <span class="truncate">{{ ville }}</span>
      </p>
      <span v-else />
      <span
        v-if="org.activities_count > 0"
        class="shrink-0 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-xs text-white"
        style="font-weight: 600 !important"
      >
        {{ $t("commun.vitrine.activites", { n: org.activities_count }, org.activities_count) }}
      </span>
    </div>
  </VitrineCarte>
</template>
